import { type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

/* -------------------------------------------------------------------------- */
/*  ExpandIcon — reusable expand button                                       */
/* -------------------------------------------------------------------------- */

export function ExpandIcon({
  onClick,
  title = '展开查看',
}: {
  onClick: () => void;
  title?: string;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M15 3h6v6" />
        <path d="M9 21H3v-6" />
        <path d="M21 3l-7 7" />
        <path d="M3 21l7-7" />
      </svg>
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/*  CloseIcon — reusable close button                                         */
/* -------------------------------------------------------------------------- */

function CloseIcon({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      title="关闭"
      onClick={onClick}
      className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M18 6 6 18" />
        <path d="M6 6l12 12" />
      </svg>
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/*  ExpandDialog — shared overlay shell                                       */
/* -------------------------------------------------------------------------- */

interface ExpandDialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  /** Optional tab bar in the header left side */
  headerExtra?: ReactNode;
  children: ReactNode;
  width?: string;
}

export function ExpandDialog({
  open,
  onClose,
  title,
  headerExtra,
  children,
  width = '480px',
}: ExpandDialogProps) {
  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in-0 duration-150"
      onClick={onClose}
    >
      <div
        className={cn(
          'relative flex max-h-[80vh] flex-col overflow-hidden rounded-xl border bg-background shadow-xl animate-in zoom-in-95 duration-150'
        )}
        style={{ width }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 border-b px-5 py-3">
          <div className="flex items-center gap-2">
            {title && <span className="text-sm font-semibold text-foreground">{title}</span>}
            {headerExtra}
          </div>
          <CloseIcon onClick={onClose} />
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>,
    document.body
  );
}
